import React, { useEffect, useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationList } from '@clerk/clerk-react';
import http from 'helpers/requests';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import { claimVerifierFirm } from 'modules/Verifier/api/verifierFirmApi';
import { fetchLegacyGrant } from 'modules/Verifier/api/verifierEngagementApi';

/**
 * Old `/audit/:grantId(/plan)` links after the audit-link migration (verifier-firm-foundation
 * phase 5 §2). Rendered inside the `isAuditPath` short-circuit in _app.jsx — before OrgBoundary —
 * because the person may have no org yet (PrivateRoute would send them to /init and lose this
 * link) or an operator workspace active.
 *
 * In order: claim (joins a pending-engagement user's firm) → look the grant up over the user's
 * OWN firms → activate that firm → go to the engagement's diagram. A firm whose setup is pending
 * then shows its Admin the "your link is now a workspace" prompt (VerifierSetupGate).
 */
export default function LegacyAuditRedirect() {
  const { t } = useTranslation('audit');
  const { grantId } = useParams();
  const location = useLocation();
  const { setActive } = useOrganizationList();
  const [target, setTarget] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!setActive) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await claimVerifierFirm().catch(() => null);
        const found = await fetchLegacyGrant(grantId);
        await setActive({ organization: found.organizationId });
        await http.refreshClerkToken();
        const plan = location.pathname.endsWith('/plan') ? '/plan' : '';
        if (!cancelled) {
          setTarget(
            `/verifier/engagements/${found.engagementId}/diagrams/${found.diagramId}${plan}`,
          );
        }
      } catch {
        if (!cancelled) setMissing(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [grantId, location.pathname, setActive]);

  if (target) return <Redirect to={target} />;
  if (missing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md rounded-xl bg-white p-6 text-center shadow-sm">
          <h1 className="m-0 mb-2 text-lg font-semibold text-slate-900">
            {t('legacy.title', 'This review link has ended')}
          </h1>
          <p className="m-0 text-sm text-slate-600">
            {t(
              'legacy.body',
              'Verifier access now goes through your firm’s workspace. Ask the operator to invite you again if you still need access.',
            )}
          </p>
        </div>
      </div>
    );
  }
  return <CgPageLoader ariaLabel={t('legacy.loading', 'Opening your engagement')} />;
}
